import { MapObject } from "@iventis/domain-model/model/mapObject";
import { convertMapObjectToLocalGeoJson } from "@iventis/map-engine/src/utilities/state-helpers";
import { LocalGeoJson, LocalGeoJsonObject } from "@iventis/map-types";

/**
 * Merges two LocalGeoJson objects into a single combined object. Does not add any objects which are already in the existing geojson.
 */
export function mergeGeojson(existingLocalGeoJson: LocalGeoJson, newLocalGeoJson: LocalGeoJson) {
    // If there is no new geojson, return the existing geojson
    if (newLocalGeoJson == null) {
        return existingLocalGeoJson;
    }

    // Copy the existing geojson so we don't mutate the original
    const copiedGeoJSON = existingLocalGeoJson != null ? { ...existingLocalGeoJson } : {};

    Object.entries(newLocalGeoJson).forEach(([layerId, newObjects]) => {
        let updatedObjects: LocalGeoJsonObject[] = [];
        const layerGeoJSON = copiedGeoJSON[layerId];
        // If there is existing geoJSON for that layer already
        if (layerGeoJSON != null) {
            updatedObjects = [...layerGeoJSON];
        }

        newObjects.forEach((newObject) => {
            // Only add objects which are not already in the existing
            if (!updatedObjects.some((existingObject) => existingObject.objectId === newObject.objectId)) {
                updatedObjects.push(newObject);
            }
        });

        copiedGeoJSON[layerId] = updatedObjects;
    });

    return copiedGeoJSON;
}

/**
 * Converts local model and distance-marker map objects to layer-keyed local GeoJSON.
 */
export function convertMapObjectsToLocalGeoJson(mapObjects: MapObject[]) {
    const localMapObjects = mapObjects.map((mapObject) => convertMapObjectToLocalGeoJson(mapObject));

    // Group the local map objects by their layer id and return as a LocalGeoJson object
    return localMapObjects.reduce<LocalGeoJson>((geoJSON, mapObject) => {
        const { layerid } = mapObject.feature.properties;
        // eslint-disable-next-line no-param-reassign
        geoJSON[layerid] = [...(geoJSON[layerid] ?? []), mapObject];
        return geoJSON;
    }, {});
}
